import {
  Button,
  Divider,
  Flex,
  NumberInput,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle, IconPlus } from "@tabler/icons-react";
import CustomText from "components/ui/CustomText/CustomText";
import { InventoryAllItemType } from "constants/types/inventory";
import { SOCreateFormValues, SOItemFormValues } from "constants/types/salesOrder";
import { FieldArray, FormikProps } from "formik";
import { memo, useCallback, useMemo, useRef } from "react";
import { emptyItem } from "../../config";
import { calcOrderTotal, formatCurrency } from "../../utils";
import SalesOrderItemRow from "../SalesOrderItemRow/SalesOrderItemRow";

interface SalesOrderItemsFieldArrayProps {
  formik: FormikProps<SOCreateFormValues>;
  inventories: InventoryAllItemType[];
}

type ProductOption = { label: string; value: string; disabled?: boolean };
type ProductOptionGroup = { group: string; items: ProductOption[] };
type SOItemField = keyof SOItemFormValues;
type SOEditableField = "quantity" | "actualPrice" | "productName";

const SalesOrderItemsFieldArray = memo(
  ({ formik, inventories }: SalesOrderItemsFieldArrayProps) => {
    const items = formik.values.items;

    const formikRef = useRef(formik);
    formikRef.current = formik;

    const productMap = useMemo(
      () =>
        inventories.reduce<Record<string, InventoryAllItemType>>((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {}),
      [inventories],
    );
    const productMapRef = useRef(productMap);
    productMapRef.current = productMap;

    // Tồn kho còn dùng được cho 1 dòng, đã trừ các dòng khác cùng sản phẩm
    const remainingStockRef = useRef(
      (index: number, productId?: string): number | undefined => {
        const rows = formikRef.current.values.items;
        const targetId = productId ?? rows[index]?.productId;
        if (!targetId) return undefined;
        const stock = productMapRef.current[targetId]?.quantity;
        if (stock === undefined) return undefined;
        const usedByOtherRows = rows.reduce(
          (sum, other, otherIndex) =>
            otherIndex !== index && other.productId === targetId
              ? sum + (Number(other.quantity) || 0)
              : sum,
          0,
        );
        return Math.max(stock - usedByOtherRows, 0);
      },
    );

    const baseProductOptions = useMemo(
      () =>
        inventories.reduce<ProductOptionGroup[]>((groups, item) => {
          const groupName = item.categoryName || "Khác";
          const existingGroup = groups.find((group) => group.group === groupName);
          const option = { label: item.name, value: item.id };

          if (existingGroup) {
            existingGroup.items.push(option);
          } else {
            groups.push({ group: groupName, items: [option] });
          }

          return groups;
        }, []),
      [inventories],
    );

    const rowQuantitiesKey = items
      .map((item) => `${item.productId}:${item.quantity}`)
      .join("|");

    // Tồn kho còn lại cho 1 dòng = tồn kho - số lượng các dòng khác cùng sản phẩm
    const rowMaxQuantities = useMemo(() => {
      return items.map((item, index) => {
        if (!item.productId) return undefined;
        const stock = productMap[item.productId]?.quantity;
        if (stock === undefined) return undefined;
        const usedByOtherRows = items.reduce(
          (sum, other, otherIndex) =>
            otherIndex !== index && other.productId === item.productId
              ? sum + (Number(other.quantity) || 0)
              : sum,
          0,
        );
        return Math.max(stock - usedByOtherRows, 0);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productMap, rowQuantitiesKey]);

    const itemsErrors = Array.isArray(formik.errors.items)
      ? (formik.errors.items as Partial<Record<SOItemField, string>>[])
      : undefined;
    const itemsTouched = Array.isArray(formik.touched.items)
      ? (formik.touched.items as Partial<Record<SOItemField, boolean>>[])
      : undefined;
    const itemsError =
      typeof formik.errors.items === "string" ? formik.errors.items : null;

    const itemsTotal = useMemo(() => calcOrderTotal(items), [items]);

    const oldDebt = Number(formik.values.oldDebt) || 0;
    const grandTotal = itemsTotal + oldDebt;

    const handleProductChange = useCallback(
      (index: number, productId: string | null) => {
        const currentItem = formikRef.current.values.items[index];
        const product = productId ? productMapRef.current[productId] : null;
        const remaining = remainingStockRef.current(index, productId ?? "");
        const nextQuantity =
          remaining !== undefined && currentItem.quantity > remaining
            ? remaining
            : currentItem.quantity;
        const nextItem: SOItemFormValues = {
          ...currentItem,
          productId: productId ?? "",
          productName: product?.name ?? "",
          productUnit: product?.unitName ?? "",
          initialPrice: product?.priceNet ?? 0,
          actualPrice: product?.priceNet ?? 0,
          quantity: nextQuantity,
        };
        formikRef.current.setFieldValue(`items[${index}]`, nextItem, false);
      },
      [],
    );

    const handleFieldChange = useCallback(
      (index: number, field: SOEditableField, value: number | string) => {
        if (field === "quantity") {
          const max = remainingStockRef.current(index);
          const nextValue =
            typeof value === "number" && max !== undefined
              ? Math.min(value, max)
              : value;
          formikRef.current.setFieldValue(`items[${index}].quantity`, nextValue, false);
          return;
        }
        formikRef.current.setFieldValue(`items[${index}].${field}`, value, false);
      },
      [],
    );

    const handleFieldBlur = useCallback((index: number, field: SOItemField) => {
      formikRef.current.setFieldTouched(`items[${index}].${field}`, true);
    }, []);

    return (
      <Stack gap="xs">
        <CustomText variant="section-title">Danh sách sản phẩm</CustomText>

        <FieldArray name="items">
          {({ push, remove }) => (
            <Stack gap="md">
              {items.map((item, index) => (
                <SalesOrderItemRow
                  key={item.rowKey}
                  index={index}
                  item={item}
                  itemError={itemsErrors?.[index]}
                  itemTouched={itemsTouched?.[index]}
                  productOptions={baseProductOptions}
                  maxQuantity={rowMaxQuantities[index]}
                  onProductChange={handleProductChange}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                  onRemove={remove}
                  canRemove={items.length > 1}
                />
              ))}

              {itemsError && (
                <Text size="xs" c="red">
                  {itemsError}
                </Text>
              )}

              <Button
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => push(emptyItem())}
                w="fit-content"
              >
                Thêm sản phẩm
              </Button>
            </Stack>
          )}
        </FieldArray>

        <Divider />

        <Stack gap={6}>
          {/* Tiền hàng hiện tại */}
          <Flex justify="space-between" align="center" px={4}>
            <Text size="sm" c="dimmed">
              Tiền hàng hiện tại
            </Text>
            <Text size="sm" c="dimmed">
              {formatCurrency(itemsTotal)}
            </Text>
          </Flex>

          {/* Công nợ cũ */}
          <Flex justify="space-between" align="center" gap="md" px={4}>
            <Flex align="center" gap={4}>
              <Text size="sm" fw={500}>
                Công nợ cũ
              </Text>
              <Tooltip
                label="Số tiền công nợ còn tồn đọng từ trước, sẽ được cộng vào tổng tiền phải trả"
                withArrow
                multiline
                w={240}
              >
                <IconInfoCircle
                  size={14}
                  style={{ color: "var(--mantine-color-dimmed)", cursor: "help" }}
                />
              </Tooltip>
            </Flex>
            <NumberInput
              placeholder="0"
              min={0}
              thousandSeparator=","
              suffix=" ₫"
              value={formik.values.oldDebt}
              hideControls
              onChange={(value) =>
                formik.setFieldValue(
                  "oldDebt",
                  typeof value === "number" ? value : 0,
                )
              }
              onBlur={() => formik.setFieldTouched("oldDebt", true)}
              error={
                formik.touched.oldDebt && formik.errors.oldDebt
                  ? formik.errors.oldDebt
                  : undefined
              }
              styles={{
                root: { width: 150 },
                input: { textAlign: "right", paddingLeft: 5 },
              }}
            />
          </Flex>

          <Divider variant="dashed" />

          {/* Tổng tiền phải trả */}
          <Flex justify="space-between" align="center" px={4}>
            <Text fw={600} size="sm">
              Tổng tiền phải trả
            </Text>
            <Text
              fw={700}
              size="md"
              c={grandTotal > itemsTotal ? "red.7" : undefined}
            >
              {formatCurrency(grandTotal)}
            </Text>
          </Flex>
        </Stack>
      </Stack>
    );
  },
  (prev, next) =>
    prev.inventories === next.inventories &&
    prev.formik.values.items === next.formik.values.items &&
    prev.formik.errors.items === next.formik.errors.items &&
    prev.formik.touched.items === next.formik.touched.items &&
    prev.formik.values.oldDebt === next.formik.values.oldDebt &&
    prev.formik.errors.oldDebt === next.formik.errors.oldDebt &&
    prev.formik.touched.oldDebt === next.formik.touched.oldDebt,
);

SalesOrderItemsFieldArray.displayName = "SalesOrderItemsFieldArray";

export default SalesOrderItemsFieldArray;
